import React, { useRef, useState } from "react"

import {
    COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG,
    OneVerifyEraUploadFileFormat,
} from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import {
    EraUploadBulkUploadStage,
    INTEGRATIONS_CONFIG,
} from "../../../config/integrations.config"
import { EraUploadHelper } from "../../../helpers/eraUpload.helper"
import type { IEraUploadFileReviewState } from "../../../types/integrations.types"

export const EraUploadFileDropzoneComponent: React.FunctionComponent<
    IEraUploadFileDropzoneComponentProps
> = props => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [stage, setStage] = useState<EraUploadBulkUploadStage>(
        EraUploadBulkUploadStage.INITIAL,
    )

    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const [validatingProgress, setValidatingProgress] = useState<{
        completed: number
        total: number
    } | null>(null)

    const x12ExtensionsLabel =
        COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.formatX12ExtensionsLabel()

    const isCsvFormat =
        props.acceptedFileFormat === OneVerifyEraUploadFileFormat.csv

    const maxFiles =
        props.maxFiles ?? INTEGRATIONS_CONFIG.eraUpload.maxFilesPerUpload

    const resetState = () => {
        setErrorMessage(null)

        setValidatingProgress(null)

        setStage(EraUploadBulkUploadStage.INITIAL)
    }

    const handleFiles = async (fileList: FileList | File[]) => {
        if (props.isDisabled) {
            return
        }

        const files = Array.from(fileList)

        if (!files.length) {
            return
        }

        setErrorMessage(null)

        if (props.singleFileOnly && files.length > 1) {
            setErrorMessage("Select only one file to replace.")

            setStage(EraUploadBulkUploadStage.ERROR)

            return
        }

        if (files.length > maxFiles) {
            setErrorMessage(
                props.singleFileOnly
                    ? "Select only one file."
                    : `You can upload up to ${maxFiles} file${maxFiles === 1 ? "" : "s"} at a time.`,
            )

            setStage(EraUploadBulkUploadStage.ERROR)

            return
        }

        setStage(EraUploadBulkUploadStage.VALIDATING)

        setValidatingProgress({
            completed: 0,
            total: files.length,
        })

        const validatedFiles = await EraUploadHelper.validateFilesForReview(
            files,
            props.acceptedFileFormat,
            {
                isCsvUploadEnabled: isCsvFormat,
                onProgress: (completed, total) => {
                    setValidatingProgress({ completed, total })
                },
            },
        )

        resetState()

        props.onFilesValidated(validatedFiles)
    }

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault()

        e.stopPropagation()
    }

    const onDrop = (e: React.DragEvent) => {
        onDragOver(e)

        if (props.isDisabled) {
            return
        }

        const droppedFiles = e.dataTransfer?.files

        if (droppedFiles?.length) {
            void handleFiles(droppedFiles)
        }
    }

    const onManualSelect = (e: React.MouseEvent) => {
        e.stopPropagation()

        if (!props.isDisabled) {
            fileInputRef.current?.click()
        }
    }

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files

        if (selectedFiles?.length) {
            void handleFiles(selectedFiles)
        }

        e.target.value = ""
    }

    const isValidating = stage === EraUploadBulkUploadStage.VALIDATING

    if (stage === EraUploadBulkUploadStage.ERROR) {
        return (
            <div className={props.className}>
                <PrimaryText
                    typography="subtitleSemibold"
                    className="
                        rounded-[8px] border border-solid border-brand-black-transparent-010
                        bg-brand-offWhite py-[16px] px-[24px]
                    "
                    color={NIRVANA_COLORS.brand.error}
                >
                    {errorMessage}
                </PrimaryText>

                <div className="flex justify-end mt-[16px]">
                    <ButtonElement
                        label="Try again"
                        type="primary"
                        htmlType="button"
                        onClick={resetState}
                    />
                </div>
            </div>
        )
    }

    if (isValidating) {
        const progressLabel =
            validatingProgress && validatingProgress.total > 1
                ? `Validating ${validatingProgress.completed} of ${validatingProgress.total} files...`
                : "Validating your ERA file..."

        return (
            <div
                className={`
                    py-[16px] px-[24px] rounded-[8px] bg-brand-offWhite flex flex-col gap-[8px]
                    border-2 border-dashed border-brand-warmShadow
                    min-h-[200px] justify-center
                    ${props.className ?? ""}
                `}
            >
                <PrimaryText typography="subtitleSemibold">
                    {progressLabel}
                </PrimaryText>

                <PrimaryText color={NIRVANA_COLORS.brand.grey7}>
                    Checking file structure before upload. Large files may take
                    a moment.
                </PrimaryText>

                <div className="flex flex-col gap-[12px] mt-[8px]">
                    {[72, 88, 64, 96, 80].map((w, i) => (
                        <div
                            key={i}
                            className="h-[16px] rounded-[4px] bg-brand-warmLight animate-pulse"
                            style={{ width: `${w}%` }}
                        />
                    ))}
                </div>
            </div>
        )
    }

    const fileNoun = props.singleFileOnly ? "file" : "files"

    const browseLabel = `Browse ${fileNoun}`

    const dropLabel = `Drag & drop your ${fileNoun} here`

    const formatSupport = isCsvFormat
        ? "Supports .csv files"
        : `Supports X12 835 files (${x12ExtensionsLabel})`

    const formatHint = props.singleFileOnly
        ? formatSupport
        : `${formatSupport} · up to ${maxFiles} files at once`

    return (
        <div className={props.className}>
            <input
                ref={fileInputRef}
                type="file"
                multiple={!props.singleFileOnly}
                accept={INTEGRATIONS_CONFIG.eraUpload.getFileAcceptForFormat(
                    props.acceptedFileFormat,
                )}
                className="hidden"
                onChange={onFileInputChange}
                disabled={props.isDisabled}
            />

            <div
                data-produktly="integrations-era-upload-dropzone"
                className={`
                    flex flex-col items-center justify-center
                    min-h-[200px] p-[32px] rounded-[8px] gap-[12px]
                    border-2 border-dashed border-brand-warmShadow bg-brand-offWhite
                    ${props.isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                `}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onClick={onManualSelect}
            >
                <PrimaryText typography="subtitleSemibold">
                    {dropLabel}
                </PrimaryText>

                <PrimaryText color={NIRVANA_COLORS.brand.grey7} centered>
                    {formatHint}
                </PrimaryText>

                <ButtonElement
                    type="primary"
                    htmlType="button"
                    label={browseLabel}
                    className="mt-[8px]"
                    disabled={props.isDisabled}
                    onClick={e => {
                        e.stopPropagation()
                        onManualSelect(e)
                    }}
                />
            </div>
        </div>
    )
}

interface IEraUploadFileDropzoneComponentProps {
    className?: string
    isDisabled?: boolean
    acceptedFileFormat: OneVerifyEraUploadFileFormat
    maxFiles?: number
    singleFileOnly?: boolean
    onFilesValidated: (files: IEraUploadFileReviewState[]) => void
}
